// ISDP CUSTOM: React binding for the recentActivity localStorage store. Uses
// useSyncExternalStore so the Home "Recently viewed" strip re-renders whenever a
// recent is recorded (this tab or another), with no polling.
import { useSyncExternalStore } from 'react';
import { getRecents, RECENT_EVENT, type RecentEntry } from './recentActivity';

// Cached snapshot so getSnapshot returns a referentially-stable value between
// changes — useSyncExternalStore requires this (else it loops / warns).
let cache: RecentEntry[] = getRecents();
let cacheRaw = JSON.stringify(cache);

function subscribe(onChange: () => void): () => void {
  const handler = () => onChange();
  // Our own writes (same tab).
  window.addEventListener(RECENT_EVENT, handler);
  // Writes from other tabs land as a native 'storage' event.
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(RECENT_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

function getSnapshot(): RecentEntry[] {
  const next = getRecents();
  const nextRaw = JSON.stringify(next);
  // Only swap the cached reference when the contents actually change.
  if (nextRaw !== cacheRaw) {
    cache = next;
    cacheRaw = nextRaw;
  }
  return cache;
}

/**
 * @returns the current recently-viewed entries (capped, de-duped, newest first).
 * Empty for brand-new users / unavailable storage.
 */
export function useRecents(): RecentEntry[] {
  return useSyncExternalStore(subscribe, getSnapshot, () => cache);
}
